import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDown, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api, ASSETS } from "@/lib/api";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";

// Fix 15 — Fallback marquee ONLY.  The live marquee content and the
// "Signature Genres" stat both come from `/api/site/signature-genres`
// (authoritative Genre Pages).  We keep a static seed so first-paint
// isn't blank while the fetch resolves; the real list replaces it
// within one render.  Homepage remains `/api/tracks`-free.
const GENRE_MARQUEE_FALLBACK = [
  "EDM (FESTIVAL / CLUB)", "AFRO HOUSE", "AFRO TECH",
  "REGGAETON", "TRAP", "TECHNO", "MOOMBAHTON",
  "BIG ROOM EDM", "FUTURE RAVE",
];

export default function Hero() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const navigate = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  // The embedded homepage catalogue was removed; the "Browse Music"
  // CTA now performs a real SPA navigation to the locale-aware
  // /music (or /ar/music) route rather than a dead #music anchor scroll.
  const goToMusic = () => navigate(localisedTo("/music", lang));

  // Fix 15 — dynamic Signature Genres from authoritative Genre Pages.
  // ``/api/site/signature-genres`` does NOT hit ``/api/tracks`` so the
  // homepage catalogue-isolation rule is preserved.  Fallback = static
  // seed until the fetch resolves.
  const [sigGenres, setSigGenres] = useState(null); // null = loading, [] = empty
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/site/signature-genres");
        if (cancelled) return;
        const items = (data?.items || [])
          .map((g) => String(g.display_name || "").toUpperCase())
          .filter(Boolean);
        setSigGenres(items);
      } catch {
        if (!cancelled) setSigGenres([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const marqueeList = sigGenres && sigGenres.length ? sigGenres : GENRE_MARQUEE_FALLBACK;
  const sigCount = sigGenres === null
    ? GENRE_MARQUEE_FALLBACK.length  // first-paint placeholder
    : sigGenres.length;

  // Hero video is a decorative background and MUST remain silent in every
  // browser/BFCache state. ffmpeg is unavailable in this environment, so
  // the source `.mov` still contains an audio stream — we enforce silence
  // via defence-in-depth on the native <video> element:
  //   * HTML attrs: `muted`, `playsInline`, `autoPlay`, `loop`
  //   * On mount + every meaningful lifecycle/visibility event, we
  //     re-assert `defaultMuted = true`, `muted = true`, `volume = 0`
  //   * `volumechange` listener re-mutes if any upstream state (BFCache
  //     restore, user preference restore, extension) ever un-mutes it
  // No user-facing sound controls are added.
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const enforceMute = () => {
      try {
        v.defaultMuted = true;
        if (!v.muted) v.muted = true;
        if (v.volume !== 0) v.volume = 0;
      } catch (_) { /* read-only in some edge cases */ }
    };
    // Initial lock
    enforceMute();
    const onPageShow = (e) => { enforceMute(); if (e && e.persisted) enforceMute(); };
    const onVisibility = () => { if (!document.hidden) enforceMute(); };
    // Lifecycle + playback events
    const events = ["loadedmetadata", "canplay", "play", "playing", "volumechange"];
    events.forEach((ev) => v.addEventListener(ev, enforceMute));
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      events.forEach((ev) => v.removeEventListener(ev, enforceMute));
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <section
      id="home"
      data-testid="section-hero"
      className="relative min-h-screen w-full overflow-hidden"
    >
      {/* Video background */}
      <div className="absolute inset-0 z-0" aria-hidden="true">
        <video
          ref={videoRef}
          data-testid="hero-video"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={ASSETS.portrait1}
          aria-label="DJ SPY live performance showcase"
          className="w-full h-full object-cover"
        >
          {/* H.264/yuv420p MP4 first (plays on Windows Chrome); the original
              HEVC .mov stays as a secondary source. */}
          <source src="/media/hero-showcase.mp4" type="video/mp4" />
          <source src={ASSETS.heroVideo} type="video/quicktime" />
          <source src={ASSETS.heroVideo} />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(239,43,43,0.18),transparent_60%)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-36 md:pt-48 pb-40">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="font-mono text-xs md:text-sm text-red-brand tracking-[0.3em] uppercase"
            >
              {t("hero.kicker")}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="font-anton mt-3 leading-[0.85] text-white text-[22vw] md:text-[15vw] lg:text-[13rem]"
            >
              {/*
                DJ / SPY must always read left-to-right regardless of the
                surrounding page direction, but the block itself has to align
                to the *start* edge (left in EN, right in AR) — otherwise it
                floats away from the column edge and looks off-center. We
                achieve this by dropping `dir="ltr"` on the <h1> (so it
                inherits `text-align: start` naturally) and isolating the
                letters with an inner LTR/bidi-isolated span.
              */}
              <span
                dir="ltr"
                style={{ unicodeBidi: "isolate", display: "inline-block" }}
              >
                DJ
                <br />
                <span className="text-red-brand">SPY</span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-6 max-w-xl text-white/75 text-lg leading-relaxed"
            >
              {t("hero.lead")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-shop-btn"
                onClick={goToMusic}
                className="btn-primary"
              >
                <Play size={16} /> {t("hero.browseMusic")}
              </button>
              <button
                data-testid="hero-book-btn"
                onClick={() => scrollTo("contact")}
                className="btn-ghost"
              >
                {t("hero.bookDjSpy")}
              </button>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.7 }}
              className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 max-w-2xl font-mono text-[10px] tracking-[0.15em] text-white/55 uppercase"
              data-testid="hero-trust-list"
              aria-label={t("hero.kicker")}
            >
              <li>{t("hero.trustList.store")}</li>
              <li>{t("hero.trustList.downloads")}</li>
              <li>{t("hero.trustList.checkout")}</li>
              <li>{t("hero.trustList.booking")}</li>
              <li>{t("hero.trustList.productions")}</li>
              <li>{t("hero.trustList.audio")}</li>
            </motion.ul>
          </div>
        </div>

        {/* Stats — both value glyphs are locked into a fixed-height,
            leading-none flex box so numerals ("19") and the infinity
            symbol ("∞") share the exact same visual baseline in every
            locale. Each column is text-center so the value glyph sits
            horizontally centred over its own label; equal column widths
            + `justify-center` mirror the pair symmetrically around the
            group centre; `mx-auto` centres the whole two-column group
            inside the hero container. */}
        <div className="mt-16 md:mt-24 mx-auto grid grid-cols-2 gap-6 font-mono text-xs md:text-sm text-white/60 max-w-md items-start">
          <div className="text-center">
            <div
              className="text-white text-2xl md:text-3xl font-display font-semibold leading-none h-8 md:h-10 flex items-center justify-center tabular-nums"
              dir="ltr"
              data-testid="hero-signature-genres-count"
            >
              {sigCount}
            </div>
            <div className="mt-2 leading-tight">{t("hero.signatureGenres")}</div>
          </div>
          <div className="text-center">
            <div className="text-white text-2xl md:text-3xl font-display font-semibold leading-none h-8 md:h-10 flex items-center justify-center tabular-nums" dir="ltr">∞</div>
            <div className="mt-2 leading-tight">{t("hero.nightsUnforgettable")}</div>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.8, repeat: Infinity }}
        className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10 text-white/50 flex flex-col items-center gap-2 font-mono text-[10px] tracking-[0.3em]"
      >
        {t("hero.scroll")}
        <ArrowDown size={16} />
      </motion.div>

      {/* Genre marquee — english labels stay LTR to preserve the aesthetic */}
      <div className="absolute bottom-0 left-0 right-0 py-4 border-t border-b border-white/10 bg-black/60 backdrop-blur-md z-20" dir="ltr">
        <Marquee gradient={false} speed={55} direction={lang === "ar" ? "right" : "left"}>
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-8 pr-8">
              {marqueeList.map((g, k) => (
                <span
                  key={g + i + k}
                  className={`font-anton text-2xl md:text-3xl tracking-widest whitespace-nowrap ${
                    k % 2 === 0 ? "stroke-text" : "text-red-brand"
                  }`}
                >
                  {g} •
                </span>
              ))}
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
